// @flow

import * as React from 'react';
import FollowingBoardsContainer from 'app/common/react/components/profile/FollowingPage/FollowingBoardsContainer';
import FollowingPeopleContainer from 'app/common/react/components/profile/ProfileFollowing/FollowingPeopleContainer';
import FollowingTopicsContainer from 'app/common/react/components/profile/FollowingPage/FollowingTopicsContainer';
import i18n from 'app/common/lib/i18n';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import withResource from 'app/common/lib/react/withResource';
import withRouteData from 'app/common/lib/react/withRouteData';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Container, SelectList } from 'gestalt';

type Props = {
  currentUser: Object,
  params: { username: string },
  requestContext: RequestContextType,
  user: {
    data: ProfileUser,
    isLoaded: boolean,
  },
  viewer: AuthViewer,
};

type State = {
  selectedOption: string,
};

type Option = {
  elementType: string,
  label: string,
  render: () => React.Element<any>,
  value: string,
};

type OptionsType = {
  Boards: Option,
  People: Option,
  Topics?: ?Option,
};

export class ProfileFollowingContainer extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { selectedOption: 'People' };
    this.options = this.initOptions();
  }

  getOptions = (): Array<{ label: string, value: string }> =>
    Object.keys(this.options).map((option) => ({
      label: this.options[option] ? this.options[option].label : '',
      value: this.options[option] ? this.options[option].value : '',
    }));

  handleSelectChange = ({ value }: { value: string }) => {
    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.USER,
      viewParameter: PCONST.thrift.ViewParameterType.USER_FOLLOWING,
      element: this.options[value].elementType,
    });
    this.setState({ selectedOption: value });
  };

  options: OptionsType;

  initOptions = (): OptionsType => {
    const { requestContext, user } = this.props;

    const profileParityEnabled = new SingleTreatmentExp(
      requestContext,
      'web_all_profiles_follow_parity',
    ).enabledWithNoActivation();

    if (profileParityEnabled && user.data && !user.data.show_creator_profile) {
      return {
        People: {
          elementType: PCONST.thrift.ElementType.USER_PROFILE_FOLLOWING_TAB_USER,
          label: i18n._('People', 'The label for the People tab on the Following page'),
          render: this.renderPeopleTab,
          value: 'People',
        },
        Boards: {
          elementType: PCONST.thrift.ElementType.USER_PROFILE_FOLLOWING_TAB_BOARD,
          label: i18n._('Boards', 'The label for the Boards tab on the Following page'),
          render: this.renderBoardsTab,
          value: 'Boards',
        },
      };
    } else {
      return {
        People: {
          elementType: PCONST.thrift.ElementType.USER_PROFILE_FOLLOWING_TAB_USER,
          label: i18n._('People', 'The label for the People tab on the Following page'),
          render: this.renderPeopleTab,
          value: 'People',
        },
        Boards: {
          elementType: PCONST.thrift.ElementType.USER_PROFILE_FOLLOWING_TAB_BOARD,
          label: i18n._('Boards', 'The label for the Boards tab on the Following page'),
          render: this.renderBoardsTab,
          value: 'Boards',
        },
        Topics: {
          elementType: PCONST.thrift.ElementType.USER_PROFILE_FOLLOWING_TAB_INTEREST,
          label: i18n._('Topics', 'The label for the Topics tab on the Following page'),
          render: this.renderTopicsTab,
          value: 'Topics',
        },
      };
    }
  };

  renderPeopleTab = () => {
    const { user } = this.props;
    return <FollowingPeopleContainer params={{ username: user.data.username }} />;
  };

  renderBoardsTab = () => {
    const { user } = this.props;
    return <FollowingBoardsContainer user={user.data} />;
  };

  renderTopicsTab = () => {
    const { user } = this.props;
    return <FollowingTopicsContainer user={user.data} />;
  };

  render() {
    const { params, user, viewer } = this.props;
    const { isLoaded } = user;
    if (!isLoaded) {
      return null;
    }
    const { selectedOption } = this.state;
    const following = this.options[selectedOption].render();
    const isOwnProfile = viewer.isAuth && params.username === viewer.username;
    const timeSpentViewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;
    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={timeSpentViewParam}
        component={PCONST.thrift.ComponentType.USER_FOLLOWING}
      >
        <Box>
          <Container>
            <Box column={12} display="flex" justifyContent="end" paddingY={2}>
              <Box flex="shrink">
                <SelectList
                  id="ProfileFollowingOptions"
                  options={this.getOptions()}
                  value={selectedOption}
                  onChange={this.handleSelectChange}
                />
              </Box>
            </Box>
          </Container>
          <Container>{following}</Container>
        </Box>
      </TimeSpent>
    );
  }
}

export default compose(
  withViewer,
  withResource({
    name: 'UserResource',
    key: 'user',
    options: ({ params }) => ({
      username: params.username,
      field_set_key: 'profile',
    }),
  }),
  withRouteData({
    name: 'ProfileFollowing',
    routeContextLog: (context: Object, params: { username: string }) => {
      let userId;
      if ((context.user || {}).username === params.username) {
        userId = context.user.id;
      }
      return {
        object_id_str: userId,
        view_type: PCONST.thrift.ViewType.USER,
        view_parameter: PCONST.thrift.ViewParameterType.USER_FOLLOWING,
      };
    },
  }),
  withRequestContext,
)(ProfileFollowingContainer);
